import { Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, forkJoin } from 'rxjs';
import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';

interface AeronaveLinha {
  codaero: string;
  tipo: string;
  modelo: string;
  fabricante: string;
  prefixo: string;
  numSerie: string;
  anoFab: string;
  proprietario: string;
  vinculo: string;
  cpfCnpj: string;
  /** Texto normalizado (sem acento, maiusculo) de todas as colunas -- calculado 1x para a busca ser instantanea. */
  busca: string;
}

/**
 * Lista de Aeronaves (cadastro AD_TCADAERO) -- tela inicial do Cadastro de Aeronave, no mesmo
 * padrao das demais listas. "Cadastrar" abre o formulario novo; duplo clique abre em edicao.
 */
@Component({
  selector: 'app-aeronaves-lista',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingIndicatorComponent],
  templateUrl: './aeronaves-lista.component.html',
  styleUrl: '../produtos-lista/produtos-lista.component.css'
})
export class AeronavesListaComponent implements OnInit {
  @Output() novaAeronave = new EventEmitter<void>();
  @Output() editarAeronave = new EventEmitter<{ prefixo: string }>();

  readonly porPagina = 14;

  todas: AeronaveLinha[] = [];
  filtradas: AeronaveLinha[] = [];
  pagina: AeronaveLinha[] = [];
  private timerBusca: any = null;
  termoBusca = '';
  carregando = false;
  erro = '';
  linhaSelecionada: AeronaveLinha | null = null;
  paginaAtual = 1;
  totalPaginas = 1;

  constructor(private sankhyaGateway: SankhyaGatewayService) {}

  ngOnInit(): void {
    void this.carregar();
  }

  async carregar(): Promise<void> {
    this.carregando = true;
    this.erro = '';
    try {
      const [res, resVinc]: any[] = await firstValueFrom(forkJoin([
        this.sankhyaGateway.listarAeronavesCadastro(),
        this.sankhyaGateway.listarVinculosAeronaves()
      ]));
      if (res?.status === '3') {
        this.erro = 'Erro ao consultar aeronaves no Sankhya: favor deslogar e logar novamente.';
        return;
      }
      // Tipos de vinculo distintos por aeronave (AD_AEROPAR).
      const rotulo: Record<string, string> = { PROPRIET: 'Proprietário', OFICINA: 'Oficina', PILOTO: 'Piloto', RESPONS: 'Responsável' };
      const vincPorAero = new Map<string, Set<string>>();
      for (const v of (resVinc?.responseBody?.result ?? []) as any[][]) {
        const cod = String(v[0] ?? '');
        const tipo = String(v[1] ?? '').trim();
        if (!cod || !tipo) continue;
        if (!vincPorAero.has(cod)) vincPorAero.set(cod, new Set());
        vincPorAero.get(cod)!.add(rotulo[tipo.toUpperCase()] ?? tipo);
      }
      const rows: any[] = res?.responseBody?.result ?? [];
      this.todas = rows
        .map((r: any[]) => {
          const codaero = String(r[0] ?? '');
          const l: AeronaveLinha = {
            codaero,
            tipo:         String(r[1] ?? ''),
            modelo:       String(r[2] ?? ''),
            fabricante:   String(r[3] ?? ''),
            prefixo:      String(r[4] ?? ''),
            numSerie:     String(r[5] ?? ''),
            anoFab:       String(r[6] ?? ''),
            proprietario: String(r[7] ?? ''),
            cpfCnpj:      String(r[8] ?? '').replace(/\D/g, ''),
            vinculo:      Array.from(vincPorAero.get(codaero) ?? []).join(', '),
            busca: ''
          };
          l.busca = this.norm([l.codaero, l.tipo, l.modelo, l.fabricante, l.prefixo, l.numSerie, l.anoFab, l.proprietario, l.vinculo].join('|'));
          return l;
        })
        .sort((a, b) => (Number(b.codaero) || 0) - (Number(a.codaero) || 0)); // mais recentes primeiro
      this.paginaAtual = 1;
      this.aplicarFiltro();
    } catch (e) {
      console.error('Erro ao listar aeronaves', e);
      this.erro = 'Não foi possível carregar a lista de aeronaves.';
    } finally {
      this.carregando = false;
    }
  }

  private norm(v: string): string {
    return (v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  }

  /** Recalcula filtro + pagina atual (1x por mudanca, nao a cada ciclo de deteccao). */
  private aplicarFiltro(): void {
    const t = this.norm(this.termoBusca.trim());
    this.filtradas = t ? this.todas.filter(a => a.busca.includes(t)) : this.todas;
    this.totalPaginas = Math.max(1, Math.ceil(this.filtradas.length / this.porPagina));
    this.paginaAtual = Math.min(this.paginaAtual, this.totalPaginas);
    this.atualizarPagina();
  }

  private atualizarPagina(): void {
    const ini = (this.paginaAtual - 1) * this.porPagina;
    this.pagina = this.filtradas.slice(ini, ini + this.porPagina);
  }

  onBuscaChange(): void {
    clearTimeout(this.timerBusca);
    this.timerBusca = setTimeout(() => {
      this.paginaAtual = 1;
      this.aplicarFiltro();
    }, 200);
  }

  irParaPagina(p: number): void {
    this.paginaAtual = Math.min(Math.max(1, p), this.totalPaginas);
    this.atualizarPagina();
  }

  trackAero(_: number, a: AeronaveLinha): string {
    return a.codaero;
  }

  codigo(a: AeronaveLinha): string {
    return a.codaero.padStart(7, '0');
  }

  onNovo(): void {
    this.novaAeronave.emit();
  }

  onEditar(a: AeronaveLinha): void {
    if (!a.prefixo) return;
    this.editarAeronave.emit({ prefixo: a.prefixo });
  }

  // ==========================================================================
  // Funcao: Comparar com o Aerosys (cadastros que faltam no Sankhya + diferencas)
  // ==========================================================================
  funcoesMenuAberto = false;
  cmpAberto = false;
  cmpCarregando = false;
  cmpImportando = false;
  cmpErro = '';
  cmpFiltro: 'FALTAM' | 'DIFERENCAS' | 'EM_ORDEM' | 'TODOS' = 'FALTAM';
  cmpBusca = '';
  cmpLinhas: CmpLinha[] = [];
  cmpResultadoImport: string[] = [];
  cmpLimite = 100;

  toggleFuncoesMenu(event?: Event): void {
    event?.stopPropagation();
    this.funcoesMenuAberto = !this.funcoesMenuAberto;
  }

  @HostListener('document:click')
  fecharMenus(): void {
    this.funcoesMenuAberto = false;
  }

  abrirComparacao(): void {
    this.funcoesMenuAberto = false;
    this.cmpAberto = true;
    this.cmpResultadoImport = [];
    void this.compararComAerosys();
  }

  fecharComparacao(): void {
    if (this.cmpImportando) return;
    this.cmpAberto = false;
  }

  private chave(matricula: string): string {
    return (matricula || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  private txtIgual(a: string, b: string): boolean {
    return this.norm((a || '').trim()).replace(/\s+/g, ' ') === this.norm((b || '').trim()).replace(/\s+/g, ' ');
  }

  async compararComAerosys(): Promise<void> {
    this.cmpCarregando = true;
    this.cmpErro = '';
    this.cmpLinhas = [];
    try {
      const resp: any = await firstValueFrom(this.sankhyaGateway.listarAeronavesAerosys());
      const lista: any[] = Array.isArray(resp) ? resp : (resp?.aeronaves ?? []);
      const sk = new Map<string, AeronaveLinha>();
      for (const a of this.todas) sk.set(this.chave(a.prefixo), a);

      const linhas: CmpLinha[] = [];
      for (const ae of lista) {
        const mat = String(ae.matricula || '').trim();
        if (!mat) continue;
        const props: any[] = ae.proprietarios || [];
        const principal = props.find(p => String(p.principal || '').toUpperCase() === 'SIM')
          || props.find(p => this.norm(String(p.tipo || '')).startsWith('PROPRIET'))
          || props[0];
        const l: CmpLinha = {
          aerId: Number(ae.aerId) || 0,
          matricula: mat,
          modelo: String(ae.modelo || ''),
          fabricante: String(ae.fabricante || ''),
          tipo: String(ae.tipo || ''),
          numSerie: String(ae.numSerie || ''),
          anoFab: ae.anoFab == null ? '' : String(ae.anoFab),
          proprietario: principal?.nome || '',
          cpfCnpj: String(principal?.cpfCnpj || '').replace(/\D/g, ''),
          proprietarios: props,
          situacao: 'FALTA',
          diferencas: [],
          codaeroSankhya: '',
          marcado: false,
          busca: ''
        };
        l.busca = this.norm([ae.aerId, mat, l.modelo, l.fabricante, l.numSerie, l.proprietario].join('|'));
        const s = sk.get(this.chave(mat));
        if (!s) {
          l.situacao = 'FALTA';
        } else {
          l.codaeroSankhya = s.codaero;
          const dif: string[] = [];
          if (l.modelo && !this.txtIgual(l.modelo, s.modelo)) dif.push(`Modelo: Aerosys "${l.modelo}" x Sankhya "${s.modelo || '—'}"`);
          if (l.fabricante && !this.txtIgual(l.fabricante, s.fabricante)) dif.push(`Fabricante: Aerosys "${l.fabricante}" x Sankhya "${s.fabricante || '—'}"`);
          if (l.numSerie && !this.txtIgual(l.numSerie, s.numSerie)) dif.push(`Nº de série: Aerosys "${l.numSerie}" x Sankhya "${s.numSerie || '—'}"`);
          if (l.anoFab && String(Number(l.anoFab)) !== String(Number(s.anoFab) || '')) dif.push(`Ano de fabricação: Aerosys "${l.anoFab}" x Sankhya "${s.anoFab || '—'}"`);
          if (l.cpfCnpj && s.cpfCnpj && l.cpfCnpj !== s.cpfCnpj) {
            dif.push(`Proprietário: Aerosys "${l.proprietario}" (${l.cpfCnpj}) x Sankhya "${s.proprietario || '—'}" (${s.cpfCnpj})`);
          } else if (l.cpfCnpj && !s.cpfCnpj) {
            dif.push(`Proprietário: Aerosys "${l.proprietario}" (${l.cpfCnpj}) x Sankhya sem proprietário`);
          }
          // Co-proprietarios/vinculos do Aerosys que nao existem no Sankhya (por vinculo tipo) -- so informativo.
          if (props.length > 1 && !s.vinculo) dif.push(`Vínculos: Aerosys tem ${props.length} pessoa(s) na aba Proprietários; Sankhya sem vínculos adicionais`);
          l.diferencas = dif;
          l.situacao = dif.length ? 'DIFERENTE' : 'OK';
        }
        linhas.push(l);
      }
      linhas.sort((a, b) => b.aerId - a.aerId);
      this.cmpLinhas = linhas;
      this.cmpFiltro = linhas.some(l => l.situacao === 'FALTA') ? 'FALTAM' : 'DIFERENCAS';
      this.cmpLimite = 100;
    } catch (e: any) {
      console.error('Erro ao comparar aeronaves', e);
      this.cmpErro = 'Não foi possível consultar as aeronaves do Aerosys: ' + (e?.error?.message || e?.message || 'erro');
    } finally {
      this.cmpCarregando = false;
    }
  }

  cmpContar(sit: 'FALTA' | 'DIFERENTE' | 'OK'): number {
    return this.cmpLinhas.filter(l => l.situacao === sit).length;
  }

  get cmpFiltrados(): CmpLinha[] {
    const t = this.norm(this.cmpBusca.trim());
    return this.cmpLinhas.filter(l => {
      if (this.cmpFiltro === 'FALTAM' && l.situacao !== 'FALTA') return false;
      if (this.cmpFiltro === 'DIFERENCAS' && l.situacao !== 'DIFERENTE') return false;
      if (this.cmpFiltro === 'EM_ORDEM' && l.situacao !== 'OK') return false;
      if (!t) return true;
      return l.busca.includes(t);
    });
  }

  get cmpSelecionados(): CmpLinha[] {
    return this.cmpLinhas.filter(l => l.marcado && l.situacao === 'FALTA');
  }

  cmpMarcarTodos(marcar: boolean): void {
    for (const l of this.cmpFiltrados) if (l.situacao === 'FALTA') l.marcado = marcar;
  }

  cmpRotulo(sit: string): string {
    return sit === 'FALTA' ? 'Não existe no Sankhya' : sit === 'DIFERENTE' ? 'Com diferenças' : 'Em ordem';
  }

  /** CODPARC (Sankhya) pelo CPF/CNPJ -- reaproveita a busca agrupada por documento do cadastro de parceiros. */
  private async codParcPorDocumento(doc: string): Promise<string | null> {
    if (!doc || /^0+$/.test(doc)) return null;
    try {
      const res: any = await firstValueFrom(this.sankhyaGateway.buscarParceirosAgrupados(doc));
      const lista: any[] = res?.lista || res?.responseBody?.parceiro || [];
      const achado = lista.find(p => String(p.docparc || '').replace(/\D/g, '') === doc) || lista[0];
      const cods: any[] = achado?.codParcs || [];
      return cods.length ? String(cods[0]) : null;
    } catch {
      return null;
    }
  }

  private tipoVinculoSankhya(tipoAerosys: string): string {
    const t = this.norm(tipoAerosys || '');
    if (t.startsWith('PROPRIET')) return 'PROPRIET';
    if (t.startsWith('RESPONS')) return 'RESPONS';
    if (t.startsWith('OFICINA')) return 'OFICINA';
    if (t.startsWith('PILOTO')) return 'PILOTO';
    return t.substring(0, 7);
  }

  async importarSelecionadas(): Promise<void> {
    const alvo = this.cmpSelecionados;
    if (!alvo.length || this.cmpImportando) return;
    this.cmpImportando = true;
    this.cmpResultadoImport = [];
    let ok = 0;
    for (const l of alvo) {
      try {
        const codParc = await this.codParcPorDocumento(l.cpfCnpj);
        if (!codParc) {
          this.cmpResultadoImport.push(`${l.matricula}: proprietário "${l.proprietario || '—'}" (${l.cpfCnpj || 'sem CPF/CNPJ'}) não cadastrado no Sankhya — cadastre o parceiro e tente novamente.`);
          continue;
        }
        const values: any = {
          '0': l.matricula.toUpperCase(),
          '1': codParc,
          '2': (l.proprietario || '').toUpperCase(),
          '3': l.cpfCnpj,
          '4': 100,
          '5': (l.fabricante || '').toUpperCase(),
          '6': l.anoFab ? Number(l.anoFab) : '',
          '7': (l.modelo || '').toUpperCase(),
          '8': (l.numSerie || '').toUpperCase()
        };
        const payload = {
          entityName: 'AD_TCADAERO',
          standAlone: false,
          fields: ['PREFIXO', 'CODPARC', 'PROPRIETARIO', 'CPFCNPJ', 'COTAPARTE', 'FABRICANTE', 'ANOFABRICACAO', 'MODELO', 'NUMEROSERIE'],
          records: [{ values }]
        };
        const res: any = await firstValueFrom(this.sankhyaGateway.salvarSankhyaPorEntidade(payload));
        if (res?.status === '3') {
          this.cmpResultadoImport.push(`${l.matricula}: erro do Sankhya — ${res?.statusMessage || 'favor deslogar e logar novamente'}.`);
          continue;
        }
        const codAero = res?.responseBody?.result?.[0]?.[0];
        if (!codAero) {
          this.cmpResultadoImport.push(`${l.matricula}: o Sankhya não retornou o código da aeronave.`);
          continue;
        }
        // Demais pessoas da aba Proprietarios do Aerosys -> AD_AEROPAR (tipo de vinculo).
        const jaCodParc = new Set<string>([codParc]);
        for (const p of l.proprietarios) {
          const doc = String(p.cpfCnpj || '').replace(/\D/g, '');
          if (!doc || doc === l.cpfCnpj) continue;
          const cp = await this.codParcPorDocumento(doc);
          if (!cp || jaCodParc.has(cp)) continue;
          jaCodParc.add(cp);
          await firstValueFrom(this.sankhyaGateway.salvarSankhyaPorEntidade({
            entityName: 'AD_AEROPAR',
            standAlone: false,
            fields: ['CODAERO', 'CODPARC', 'TIPO_VINCULO'],
            records: [{ values: { '0': codAero, '1': cp, '2': this.tipoVinculoSankhya(p.tipo) } }]
          }));
        }
        ok++;
        l.situacao = 'OK';
        l.marcado = false;
        l.codaeroSankhya = String(codAero);
      } catch (e: any) {
        console.error('Erro ao importar aeronave', l.matricula, e);
        this.cmpResultadoImport.push(`${l.matricula}: ${e?.error?.message || e?.message || 'erro ao gravar no Sankhya'}.`);
      }
    }
    this.cmpResultadoImport.unshift(`${ok} aeronave(s) importada(s) para o Sankhya.`);
    this.cmpImportando = false;
    await this.carregar();
    await this.compararComAerosys();
  }
}

interface CmpLinha {
  aerId: number;
  matricula: string;
  modelo: string;
  fabricante: string;
  tipo: string;
  numSerie: string;
  anoFab: string;
  proprietario: string;
  cpfCnpj: string;
  proprietarios: any[];
  situacao: 'FALTA' | 'DIFERENTE' | 'OK';
  diferencas: string[];
  codaeroSankhya: string;
  marcado: boolean;
  busca: string;
}
